import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createUploadUrl,
  deleteAsset,
  listStudioAssets,
  saveAsset,
  UploadCancelled,
  uploadToTarget,
} from "../../data/client";
import type { Asset, AssetTypeDef, SlotDef } from "../../data/types";
import { Button, Chip, Display, Panel, PipPicker, Sprite } from "../../design/primitives";
import { capFor, isImageSlot } from "../../data/uploadLimits";
import { art, cardSprite } from "../../design/sprites";
import { CARD_WASH, shade } from "../../design/shade";
import { EmptyPickerNote } from "./EmptyPickerNote";
import { useNavigationGuard } from "./NavigationGuard";
import { useRecordLabel } from "./RecordLabel";
import { StudioActions } from "./StudioActions";
import { useStudio } from "./StudioContext";
import { LoadingState } from "../states";
import { slugify, uniqueSlug } from "../../data/slug";
import css from "./studio.module.css";
import form from "./assetForm.module.css";

type SlotState =
  | { phase: "uploading"; percent: number; name: string }
  | { phase: "failed"; message: string; file: File }
  | { phase: "rejected"; message: string };

/**
 * The asset form - §11.3.
 *
 * The organising rule: **the asset type decides the form.** Which metadata
 * fields render, which file slots exist, what the screen is called and which
 * pixel object the card uses all come from the selected type (D-10), never
 * from anything hardcoded to tabs.
 */

const MAX_BYTES = 25 * 1024 * 1024;

const blankAsset = (creatorId: string, type: AssetTypeDef): Asset => ({
  id: `asset-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
  creatorId,
  slug: "",
  slugHistory: [],
  assetType: type.type,
  title: "",
  gameId: "",
  tags: [],
  note: "",
  status: "draft",
  minTier: "",
  fields: {},
  files: {},
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

export default function AssetForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { creator } = useStudio();

  const [asset, setAsset] = useState<Asset | null>(null);
  /* The asset exactly as loaded. Dirty is a comparison against this, never a
   * touched flag (FR-16): a blank form writes its defaults at mount, so a
   * touched flag would call it dirty immediately, CANCEL would always prompt,
   * and the author would learn to dismiss the prompt - which is worse than no
   * guard, because it trains dismissal of the real ones too. */
  const [loaded, setLoaded] = useState<Asset | null>(null);
  const { register } = useNavigationGuard();
  /* `persist` is defined below the loading early-return, so the guard's
   * callback reaches it through a ref rather than the component being
   * restructured around a hook-ordering rule. */
  const persistRef = useRef<null | (() => Promise<void>)>(null);
  const [siblings, setSiblings] = useState<Asset[]>([]);
  const [slugEdited, setSlugEdited] = useState(false);
  /*
   * Null when idle, otherwise the line to print while the request is in
   * flight - "PUBLISHING…", "SAVING…". A boolean disabled the buttons and said
   * nothing, so a slow save looked like a dead button: the author pressed
   * PUBLISH, nothing moved, and there was no way to tell a request in flight
   * from one that never started.
   */
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [badSlots, setBadSlots] = useState<string[]>([]);
  /*
   * FR-31's four row states, one per slot.
   *
   * `failed` keeps the File so RETRY does not ask for it again - having to
   * re-pick the file is what makes a failed upload feel like lost work.
   * `rejected` deliberately has no file: the wrong type or an oversized file
   * cannot succeed on retry, so offering one would be a lie.
   */
  const [slots, setSlots] = useState<Record<string, SlotState>>({});
  const aborts = useRef<Record<string, AbortController>>({});
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const types = creator.assetTypes.filter((t) => !t.retired);

  useEffect(() => {
    let live = true;
    listStudioAssets().then((all) => {
      if (!live) return;
      setSiblings(all);
      if (id === "new") {
        const blank = blankAsset(creator.creatorId, types[0]);
        setAsset(blank);
        setLoaded(blank);
        setSlugEdited(false);
      } else {
        const found = all.find((a) => a.id === id) ?? blankAsset(creator.creatorId, types[0]);
        setAsset(found);
        setLoaded(found);
        setSlugEdited(true);
      }
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const typeDef = useMemo(
    () => creator.assetTypes.find((t) => t.type === asset?.assetType) ?? types[0],
    [creator.assetTypes, asset?.assetType, types]
  );

  /**
   * A retired game stays selectable while it **is** the current selection
   * (D-15). Without this, opening an asset whose game was retired and saving
   * would silently repoint it at whatever the picker defaulted to.
   */
  const selectableGames = useMemo(() => {
    const active = creator.games.filter((g) => !g.retired);
    const current = creator.games.find((g) => g.id === asset?.gameId);
    return current && current.retired ? [current, ...active] : active;
  }, [creator.games, asset?.gameId]);

  /*
   * A structural comparison, not a touched flag (FR-16). JSON is adequate here
   * because an Asset is plain data - strings, numbers, booleans and arrays of
   * them - with no Date, Map or undefined to lose in the round trip. If that
   * stops being true this needs a real deep-equal, not a wider try/catch.
   */
  const formDirty = asset !== null && loaded !== null &&
    JSON.stringify(asset) !== JSON.stringify(loaded);

  /*
   * Names this screen in the breadcrumb (FR-4). Above the `!asset` early
   * return, because it is a hook - and `undefined` while loading is right:
   * the trail reads UNTITLED for the moment before the record arrives rather
   * than showing the title of whatever was open last.
   *
   * The *live* title, not the saved one, so renaming an asset renames the
   * trail as you type. The breadcrumb describes what is on screen.
   */
  useRecordLabel(asset?.title || undefined);

  useEffect(() => {
    if (!asset) return;
    register({
      dirty: formDirty,
      // Continuing from the guard saves a draft: publishing has requirements
      // that an interrupted edit may not meet, and a guard is no place to
      // discover them.
      save: async () => {
        await persistRef.current?.();
      },
      saveLabel: "SAVE DRAFT",
      discard: () => setAsset(loaded),
    });
    return () => register(null);
  }, [register, formDirty, asset, loaded]);

  if (!asset) return <LoadingState label="OPENING" subLabel="LOADING THE ASSET" />;

  const game = creator.games.find((g) => g.id === asset.gameId);
  const tint = game?.tint ?? "#8A7FB0";
  const patch = (next: Partial<Asset>) => setAsset({ ...asset, ...next });

  const setTitle = (title: string) => {
    // Slug follows the title until it is edited by hand, then it stops moving.
    const taken = siblings.filter((s) => s.id !== asset.id).map((s) => s.slug);
    patch({
      title,
      ...(slugEdited ? {} : { slug: uniqueSlug(slugify(title), taken) }),
    });
  };

  /** Switching type replaces the whole shape of the form, so files go (D-10). */
  const switchType = (next: AssetTypeDef) => {
    if (next.type === asset.assetType) return;
    patch({ assetType: next.type, files: {}, fields: {} });
    setBadSlots([]);
    // An upload in flight belongs to the old type's slot. Abort it rather than
    // let it land on a form that no longer has that slot.
    Object.values(aborts.current).forEach((c) => c.abort());
    setSlots({});
  };

  /** Clears a slot's row state, returning it to empty-or-attached. */
  const clearSlot = (slotId: string) =>
    setSlots((s) => {
      const next = { ...s };
      delete next[slotId];
      return next;
    });

  const upload = async (slot: SlotDef, file: File) => {
    clearSlot(slot.id);

    /*
     * Refusals first, and they are terminal.
     *
     * Both are checked again on the server - which signs ContentLength, so the
     * cap is enforced by S3 rather than by trusting this number. Checking here
     * buys a specific message before a round trip, nothing more.
     *
     * These become `rejected` rather than `failed` because no retry can help:
     * the file is the wrong file. Offering RETRY would be an invitation to
     * press it repeatedly.
     */
    if (file.size > MAX_BYTES) {
      setSlots((s) => ({
        ...s,
        [slot.id]: { phase: "rejected", message: "TOO LARGE — 25 MB MAXIMUM" },
      }));
      return;
    }
    const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
    if (slot.accept.length && !slot.accept.includes(ext)) {
      setSlots((s) => ({
        ...s,
        // §13.2: name what IS accepted, not just that this was not.
        [slot.id]: {
          phase: "rejected",
          message: `ACCEPTS ${slot.accept.join(", ").toUpperCase()}`,
        },
      }));
      return;
    }

    const controller = new AbortController();
    aborts.current[slot.id] = controller;
    const contentType = file.type || "application/octet-stream";
    setSlots((s) => ({
      ...s,
      [slot.id]: { phase: "uploading", percent: 0, name: file.name },
    }));

    try {
      const target = await createUploadUrl(asset.id, asset.assetType, slot, {
        name: file.name,
        size: file.size,
        type: contentType,
      });
      await uploadToTarget(target, file, {
        contentType,
        signal: controller.signal,
        onProgress: (percent) =>
          setSlots((s) =>
            // Guarded: a late progress event after a cancel would otherwise
            // put the row back into `uploading`.
            s[slot.id]?.phase === "uploading"
              ? { ...s, [slot.id]: { phase: "uploading", percent, name: file.name } }
              : s
          ),
      });

      clearSlot(slot.id);
      patch({
        files: {
          ...asset.files,
          [slot.id]: { key: target.key, name: file.name, size: file.size, contentType },
        },
      });
    } catch (e) {
      // Cancelling is a choice. The row goes back to empty and says nothing.
      if (e instanceof UploadCancelled) {
        clearSlot(slot.id);
        return;
      }
      /*
       * §13.2 failed state: say where it stopped, that nothing was saved -
       * and *why*. The bare message hid a plain 404 from the signing call and
       * turned a five-minute fix into a bug report (2026-08-31). Same lesson
       * as the token error App.tsx used to swallow before task 9.
       */
      const why = (e as Error).message || "UNKNOWN";
      console.error(`[upload] ${slot.id} failed:`, e);
      setSlots((s) => ({
        ...s,
        // The File is kept so RETRY does not make the user find it again.
        [slot.id]: {
          phase: "failed",
          message: `UPLOAD FAILED — NOTHING WAS SAVED · ${why.toUpperCase()}`,
          file,
        },
      }));
    } finally {
      delete aborts.current[slot.id];
    }
  };

  // Saving and uploading both block the footer buttons, for different reasons.
  const uploading = Object.values(slots).some((v) => v.phase === "uploading");

  persistRef.current = async () => {
    await persist({ ...asset, status: "draft" }, {
      working: "SAVING…",
      done: "SAVED AS A DRAFT",
    });
  };

  /**
   * The one write path. `working` is printed while the request is in flight;
   * `done` is the confirmation afterwards.
   *
   * `leaveForList` sends a successful publish back to the asset list, carrying
   * the confirmation with it (FR-11): the line has to appear where the author
   * lands, or publishing reads as having done nothing.
   */
  const persist = async (
    next: Asset,
    { working, done, leaveForList = false }:
      { working: string; done: string; leaveForList?: boolean }
  ) => {
    setBusy(working);
    setRefusal(null);
    // The previous confirmation is about the previous save. Leaving it up
    // beside a new one in flight claims this one has already succeeded.
    setConfirmation(null);
    try {
      const saved = await saveAsset(next);
      setAsset(saved);
      /*
       * Both, always. `loaded` is what dirty is measured against (FR-16), so
       * leaving it at the pre-save record kept the form dirty after a
       * *successful* save - which meant the unsaved-changes guard fired on the
       * way out of a form with nothing unsaved in it.
       */
      setLoaded(saved);
      if (leaveForList) {
        // Last, so nothing else in this handler runs against an unmounted
        // screen.
        navigate("/studio", { state: { notice: done } });
        return;
      }
      setConfirmation(done);
    } catch (e) {
      setRefusal(`COULD NOT SAVE — ${(e as Error).message.toUpperCase()}`);
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    const missing = typeDef.slots.filter((s) => s.required && !asset.files[s.id]);
    if (missing.length > 0) {
      // §13.3: refusal mirrors confirmation - same header line, maroon instead
      // of teal, plus an outline on the offending slot. No toast, no modal.
      setBadSlots(missing.map((s) => s.id));
      setRefusal(`CAN'T PUBLISH — ${missing.map((s) => s.label).join(", ").toUpperCase()} IS REQUIRED`);
      return;
    }
    if (!asset.minTier) {
      setRefusal("CAN'T PUBLISH — CHOOSE AN AUDIENCE TIER");
      return;
    }
    setBadSlots([]);

    const tier = creator.tiers.find((t) => t.id === asset.minTier);
    const formats = typeDef.slots
      .filter((s) => s.shortName && asset.files[s.id])
      .map((s) => s.shortName);

    await persist(
      {
        ...asset,
        status: "published",
        // Set at FIRST publish, then left alone (D-11).
        addedAt: asset.addedAt ?? new Date().toISOString(),
      },
      {
        working: "PUBLISHING…",
        done: `PUBLISHED TO ${tier?.name.toUpperCase()} AND ABOVE · ${formats.join(" + ")}`,
        // Publishing finishes the job; staying on the form implies there is
        // more to do here. Saving a draft does not - you are still editing.
        leaveForList: true,
      }
    );
  };

  const audienceCount = (tierId: string) => {
    const tier = creator.tiers.find((t) => t.id === tierId);
    if (!tier) return 0;
    // "and above" is by price (D-5), so the reach is every tier at or over it.
    return creator.tiers
      .filter((t) => t.amountCents >= tier.amountCents)
      .reduce((sum, t) => sum + t.patrons, 0);
  };

  const subLine = typeDef.slots
    .filter((s) => s.shortName)
    .map((s) => s.shortName)
    .join(" / ");

  /*
   * In-flight first. A request under way outranks the outcome of the last one,
   * and the same mono line already carries UPLOADING… - so this is the
   * existing pattern, not a new one.
   */
  const status = busy ? (
    <span className={css.label}>{busy}</span>
  ) : refusal ? (
    <span className={css.refuse}>{refusal}</span>
  ) : confirmation ? (
    <span className={css.confirm}>{confirmation}</span>
  ) : uploading ? (
    <span className={css.label}>UPLOADING…</span>
  ) : null;

  return (
    <>
      <StudioActions status={status}>
        <Button
          variant="secondary"
          size="rail"
          disabled={busy !== null}
          /*
           * CANCEL, not DISCARD - the spec left this open. On a published asset
           * "discard" can be read as discarding the publication rather than the
           * edit, and that is the reading that loses work.
           *
           * Today it leaves without asking, exactly as the browser back button
           * already did. Task 6 gives it the unsaved-changes prompt.
           */
          onClick={() => navigate("/studio")}
        >
          CANCEL
        </Button>

        {id !== "new" && (
          <button
            type="button"
            className={form.deleteLink}
            onClick={() => setConfirmingDelete(true)}
          >
            Delete this asset
          </button>
        )}

        {asset.status === "published" && (
          // §13.4: one click, reversible, no confirmation.
          <Button
            variant="secondary"
            size="rail"
            disabled={busy !== null || uploading}
            onClick={() =>
              persist({ ...asset, status: "draft" }, {
                working: "UNPUBLISHING…",
                done: "UNPUBLISHED — NOW A DRAFT",
              })
            }
          >
            UNPUBLISH
          </Button>
        )}

        <Button
          variant="secondary"
          size="rail"
          disabled={busy !== null || uploading}
          onClick={() =>
            persist({ ...asset, status: "draft" }, {
              working: "SAVING…",
              done: "SAVED AS A DRAFT",
            })
          }
        >
          SAVE DRAFT
        </Button>

        <Button
          variant="gold"
          size="rail"
          onClick={publish}
          disabled={busy !== null || uploading}
        >
          PUBLISH
        </Button>
      </StudioActions>

      {/*
        §13.4: inline, maroon-bordered, never a modal. It sits under the
        breadcrumb rather than in the bar because a three-line question does not
        belong in a row of buttons.
      */}
      {confirmingDelete && (
        <div className={form.deleteConfirm}>
          <p className={form.deleteHeading}>DELETE “{asset.title || "UNTITLED"}”?</p>
          <p className={form.deleteBody}>
            It goes to Recently deleted for 30 days, and downloads already in
            flight will finish.
          </p>
          <div className={form.chips}>
            <Button variant="secondary" size="rail" onClick={() => setConfirmingDelete(false)}>
              KEEP IT
            </Button>
            <Button
              variant="destructive"
              size="rail"
              onClick={async () => {
                await deleteAsset(asset.id);
                navigate("/studio");
              }}
            >
              DELETE
            </Button>
          </div>
        </div>
      )}

      <div className={css.head}>
        {/*
          Always "ASSET" (FR-6, D-33, superseding D-15). The type is chosen
          *inside* this form by the switcher below, so at the moment the title
          renders there is nothing to derive a noun from. `typeDef.headingNoun`
          survives as data and still names the type in the sub-line.
        */}
        <Display size={38} as="h1">
          {id === "new" ? "NEW ASSET" : asset.title.toUpperCase() || "UNTITLED"}
        </Display>
        <span className={css.label}>
          ASSET TYPE: {typeDef.label.toUpperCase()} — {subLine}
        </span>
        <div className={css.toolbarRight}>
          {/*
            Outcome moved to the action zone (FR-11): a refusal belongs beside
            the button that refused, not at the other end of the screen from it.
          */}
        </div>
      </div>

      <div className={form.split}>
        <div className={form.left}>
          {/* §11.3: the type switcher sits above everything. */}
          <div className={form.field}>
            <span className={css.label}>ASSET TYPE</span>
            <div className={form.chips}>
              {types.map((t) => (
                <Chip
                  key={t.type}
                  active={t.type === asset.assetType}
                  onClick={() => switchType(t)}
                >
                  {t.label.toUpperCase()}
                </Chip>
              ))}
            </div>
          </div>

          <div className={form.field}>
            <span className={css.label}>TITLE</span>
            <input
              className={css.input}
              value={asset.title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className={form.field}>
            <span className={css.label}>GAME</span>
            {selectableGames.length === 0 ? (
              <EmptyPickerNote
                message="No games yet. Games carry the franchise and the card tint, and are authored on"
                linkLabel="LISTS"
                to="/studio/lists"
              />
            ) : (
            <select
              className={css.input}
              value={asset.gameId}
              onChange={(e) => patch({ gameId: e.target.value })}
            >
              <option value="">Choose a game…</option>
              {selectableGames.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                  {g.retired ? " · RETIRED" : ""}
                </option>
              ))}
            </select>
            )}
            {game && (
              // Franchise and tint are the game's, shown read-only. There is no
              // franchise field and no tint swatch here (D-8 superseded).
              <div className={form.derived}>
                <span className={css.label}>DERIVED FROM THE GAME</span>
                <span className={form.derivedValue}>{game.franchise || "no franchise"}</span>
                <span className={form.tintDot} style={{ background: game.tint }} />
              </div>
            )}
          </div>

          <div className={form.field}>
            <span className={css.label}>SLUG</span>
            <input
              className={css.input}
              value={asset.slug}
              onChange={(e) => {
                setSlugEdited(true);
                patch({ slug: e.target.value });
              }}
            />
            <span className={form.hint}>
              /tabs/{asset.slug || "…"} — old slugs keep redirecting
            </span>
          </div>

          {/* Fields come from the type (D-10). */}
          {typeDef.fields.map((field) =>
            field.kind === "pips" ? (
              <div className={form.field} key={field.id}>
                <span className={css.label}>{field.label.toUpperCase()}</span>
                <PipPicker
                  size={22}
                  value={Number(asset.fields[field.id] ?? 0)}
                  onChange={(v) => patch({ fields: { ...asset.fields, [field.id]: v } })}
                />
              </div>
            ) : (
              <div className={form.field} key={field.id}>
                <span className={css.label}>{field.label.toUpperCase()}</span>
                <input
                  className={css.input}
                  value={String(asset.fields[field.id] ?? "")}
                  onChange={(e) =>
                    patch({ fields: { ...asset.fields, [field.id]: e.target.value } })
                  }
                />
              </div>
            )
          )}

          <div className={form.field}>
            <span className={css.label}>PERFORMANCE URL — OPTIONAL</span>
            <input
              className={css.input}
              value={asset.performanceUrl ?? ""}
              placeholder="https://www.youtube.com/watch?v=…"
              onChange={(e) => patch({ performanceUrl: e.target.value || undefined })}
            />
            <span className={form.hint}>Empty hides the link on the patron side.</span>
          </div>

          <div className={form.field}>
            <span className={css.label}>TAGS</span>
            <div className={form.chips}>
              {creator.tags
                .filter((t) => !t.retired || asset.tags.includes(t.id))
                .map((tag) => (
                  <Chip
                    key={tag.id}
                    active={asset.tags.includes(tag.id)}
                    onClick={() =>
                      patch({
                        tags: asset.tags.includes(tag.id)
                          ? asset.tags.filter((t) => t !== tag.id)
                          : [...asset.tags, tag.id],
                      })
                    }
                  >
                    {tag.label.toUpperCase()}
                    {tag.retired ? " · RETIRED" : ""}
                  </Chip>
                ))}
              {creator.tags.length === 0 && (
                <span className={form.hint}>No tags yet — add some under LISTS.</span>
              )}
            </div>
          </div>

          <div className={form.field}>
            <span className={css.label}>
              NOTE FROM {creator.displayName.split(" ")[0].toUpperCase()}
            </span>
            <textarea
              className={css.tierBlurb}
              rows={4}
              value={asset.note}
              onChange={(e) => patch({ note: e.target.value })}
            />
          </div>

          {/* Slots come from the type (D-10). */}
          <div className={form.field}>
            {/*
              The cap is per slot since `asset-cover-art` — images are 5 MB
              because a cover is drawn on every card in the grid, everything
              else is 25 MB. The heading used to state one number for all of
              them and was wrong the moment that changed, so the limit moved
              onto the rows that own it.
            */}
            <span className={css.label}>FILES</span>
            {typeDef.slots.map((slot) => {
              const stored = asset.files[slot.id];
              const state = slots[slot.id];
              // A row in flight is neither filled nor empty; it is busy.
              const bad =
                badSlots.includes(slot.id) ||
                state?.phase === "failed" ||
                state?.phase === "rejected";
              return (
                <div
                  key={slot.id}
                  className={`${form.slot} ${stored ? form.slotFilled : form.slotEmpty} ${bad ? form.slotBad : ""}`}
                >
                  <Sprite
                    src={slot.shortName === "PDF" ? art.pdf : art.gp}
                    size={20}
                    className={stored ? undefined : form.slotIconEmpty}
                  />
                  <span className={form.slotLabel}>
                    {slot.label}
                    {slot.required && " *"}
                    {/*
                      Reported by Adam 2026-09-13: "I really don't understand
                      where I'm supposed to actually upload. There's nothing
                      that says change the thumbnail."

                      He was looking at this row. "Cover art" is a slot that has
                      existed since the design system landed and did nothing,
                      and its name never said what it was for - so a feature
                      that makes it the card art is invisible to the person
                      using it. The label is creator data, so the explanation
                      goes here rather than into the stored record.
                    */}
                    <span className={form.slotHint}>
                      {isImageSlot(slot.accept)
                        ? "shown on the card — max 5 MB"
                        : `max ${Math.round(capFor(slot) / (1024 * 1024))} MB`}
                    </span>
                  </span>
                  {/* FR-31's four states. Exactly one of these renders. */}
                  {state?.phase === "uploading" ? (
                    <>
                      <span className={form.slotFile}>{state.name}</span>
                      <span
                        className={form.progress}
                        role="progressbar"
                        aria-valuenow={state.percent}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`Uploading ${state.name}`}
                      >
                        <span
                          className={form.progressFill}
                          style={{ width: `${state.percent}%` }}
                        />
                      </span>
                      <span className={form.slotNoFile}>{state.percent}%</span>
                    </>
                  ) : state?.phase === "failed" ? (
                    <>
                      <span className={form.slotFile}>{state.file.name}</span>
                      <span className={css.refuse}>{state.message}</span>
                    </>
                  ) : state?.phase === "rejected" ? (
                    <span className={css.refuse}>{state.message}</span>
                  ) : (
                    <span className={stored ? form.slotFile : form.slotNoFile}>
                      {stored ? stored.name : "No file attached"}
                    </span>
                  )}

                  {state?.phase === "uploading" && (
                    <button
                      type="button"
                      className={css.miniButton}
                      style={{ marginLeft: "auto" }}
                      onClick={() => aborts.current[slot.id]?.abort()}
                    >
                      CANCEL
                    </button>
                  )}

                  {state?.phase === "failed" && (
                    <button
                      type="button"
                      className={css.miniButton}
                      style={{ marginLeft: "auto" }}
                      // The File was kept, so this needs nothing from the user.
                      onClick={() => upload(slot, state.file)}
                    >
                      RETRY
                    </button>
                  )}

                  <label
                    className={css.miniButton}
                    style={state?.phase === "uploading" || state?.phase === "failed" ? undefined : { marginLeft: "auto" }}
                    hidden={state?.phase === "uploading"}
                  >
                    {stored || state?.phase === "failed" ? "REPLACE" : "CHOOSE FILE"}
                    <input
                      type="file"
                      hidden
                      accept={slot.accept.join(",")}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) upload(slot, f);
                      }}
                    />
                  </label>
                  {stored && !state && (
                    <button
                      type="button"
                      className={form.remove}
                      onClick={() => {
                        const files = { ...asset.files };
                        delete files[slot.id];
                        patch({ files });
                      }}
                    >
                      REMOVE
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ---- right rail ------------------------------------------------ */}
        <aside className={form.rail}>
          <Panel style={{ padding: "var(--space-20)" }}>
            <span className={css.label}>AUDIENCE</span>
            <div className={form.tierButtons}>
              {creator.tiers.map((tier) => (
                <Button
                  key={tier.id}
                  variant={asset.minTier === tier.id ? "gold" : "secondary"}
                  size="rail"
                  style={{ width: "100%", justifyContent: "flex-start" }}
                  onClick={() => patch({ minTier: tier.id })}
                >
                  {tier.name}
                </Button>
              ))}
              {creator.tiers.length === 0 && (
                <EmptyPickerNote
                  message="No tiers yet. They come from Patreon and are synced on"
                  linkLabel="TIERS & ACCESS"
                  to="/studio/tiers"
                />
              )}
            </div>
            {asset.minTier && (
              <p className={css.confirm} style={{ marginTop: "var(--space-12)" }}>
                VISIBLE TO {creator.tiers.find((t) => t.id === asset.minTier)?.name.toUpperCase()}{" "}
                AND ABOVE — {audienceCount(asset.minTier)} PATRONS
              </p>
            )}
          </Panel>

          <Panel style={{ padding: "var(--space-20)" }}>
            <span className={css.label}>CARD PREVIEW</span>
            {/* The real card art, so the author can see that a new game needs a
                hue and nothing else. */}
            <div className={form.preview} style={{ background: shade(tint, CARD_WASH) }}>
              <Sprite src={cardSprite(typeDef.cardObject, tint)} size={132} />
            </div>
            <div className={form.previewBody}>
              <strong>{asset.title || "Untitled"}</strong>
              <span>{game?.name ?? "No game"}</span>
            </div>
          </Panel>

          {/*
            FR-8: nothing that persists anything lives in a content column. The
            rail holds no actions at all - they are declared below and rendered
            in the shell's one bar.
          */}
        </aside>
      </div>
    </>
  );
}
